import React, { useState } from "react";
import { X, Users, User, Search } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { GlobalService } from "../../modules";
import { assignProfessionalWhatsApp } from "../../redux/Actions/AuthAction";
import { useAppDispatch } from "../../redux";
import { createQuote } from "../../redux/Actions/QuoteActions";
import { createUserNotificationAction } from "../../redux/Actions/UserNotificationActions";
import { QuoteStatusEnum, PurchaseQuoteTypeEnum } from "../../modules/shared/model/quote.enum";
import { NotificationStatusEnum, NotificationPriorityEnum, NotificationTypeEnum } from "../../modules/shared/model/user-notification.enum";

interface EmergencyProfessionalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: any;
  emergencyProfessionals: any[];
  professionalsLoading: boolean;
  emergencyProfessionalsTotal: number;
  emergencyProfessionalsPage: number;
  emergencyProfessionalsTotalPages: number;
  professionalsPage: number;
  setProfessionalsPage: (page: number) => void;
  onAssignmentSuccess?: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const EmergencyProfessionalsModal: React.FC<EmergencyProfessionalsModalProps> = ({
  isOpen,
  onClose,
  request,
  emergencyProfessionals,
  professionalsLoading,
  emergencyProfessionalsTotal,
  emergencyProfessionalsPage,
  emergencyProfessionalsTotalPages,
  professionalsPage,
  setProfessionalsPage,
  onAssignmentSuccess,
  searchQuery,
  setSearchQuery
}) => {
  const dispatch = useAppDispatch();
  const [selectedProfessionals, setSelectedProfessionals] = useState<Set<string>>(new Set());
  const [isAssigning, setIsAssigning] = useState(false);

  if (!isOpen || !request) return null;

  // Function to mask private information
  const maskPrivateInfo = (info: any, type: string) => {
    // Handle null, undefined, or empty strings
    if (!info || typeof info !== "string" || info.trim() === "") {
      return "N/A";
    }

    switch (type) {
      case "name":
        return info.replace(/(?!^.).(?!$)/g, "*");
      case "email": {
        if (!info.includes("@")) {
          return "N/A";
        }
        const [localPart, domain] = info.split("@");
        if (!localPart || !domain || !domain.includes(".")) {
          return "N/A";
        }
        const domainParts = domain.split(".");
        return `${localPart[0]}***@${domainParts[0][0]}***.${domainParts[1] || "com"}`;
      }
      case "phone":
        return info.replace(/\d(?=\d{4})/g, "*");
      case "address":
        return info.replace(/\d+|[a-zA-Z]+(?= )/g, "***");
      default:
        return info;
    }
  };

  // Handle individual professional selection
  const handleSelectProfessional = (professionalId: string, hasWhatsApp: boolean) => {
    if (!hasWhatsApp) {
      GlobalService.showToast("warning", "Warning", "This professional doesn't have a WhatsApp number and cannot be selected");
      return;
    }

    const newSelected = new Set(selectedProfessionals);
    if (newSelected.has(professionalId)) {
      newSelected.delete(professionalId);
    } else {
      newSelected.add(professionalId);
    }
    setSelectedProfessionals(newSelected);
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    const professionalsWithWhatsApp = emergencyProfessionals.filter(p => p.whatsappNumber);
    if (selectedProfessionals.size === professionalsWithWhatsApp.length) {
      setSelectedProfessionals(new Set());
    } else {
      setSelectedProfessionals(new Set(professionalsWithWhatsApp.map(p => p.id)));
    }
  };

  // Handle bulk assignment
  const handleBulkAssignment = async () => {
    if (selectedProfessionals.size === 0) {
      GlobalService.showToast("warning", "Warning", "Please select at least one professional");
      return;
    }

    try {
      setIsAssigning(true);
      const selectedPros = emergencyProfessionals.filter(p => selectedProfessionals.has(p.id));
      
      for (const professional of selectedPros) {
        try {
          // 1. Send WhatsApp notification (non-blocking - errors won't stop the process)
          if (professional.whatsappNumber) {
            try {
              const whatsAppData = {
                to: professional.whatsappNumber,
                var1_name: `${professional.firstName} ${professional.lastName}`,
                var2_jobTitle: request.title || "Emergency Job",
                var3_jobDescription: (request.description || "Emergency service request").replace(/\s*\n\s*/g, " ").trim(),
                var4_clientName: maskPrivateInfo(request.customer?.name, "name"),
                var5_clientPhone: maskPrivateInfo(request.customer?.phone, "phone"),
                var6_clientWhatsapp: maskPrivateInfo(request.customer?.whatsappNumber, "phone"),
                var7_clientEmail: maskPrivateInfo(request.customer?.email, "email"),
                var8_clientAddress: maskPrivateInfo(request.customer?.address, "address"),
                var9_detailsUrl: "https://kokkobeo.com/myproposals"
              };

              await dispatch(assignProfessionalWhatsApp(whatsAppData));
            } catch (whatsappError) {
              console.warn(`WhatsApp notification failed for ${professional.firstName} ${professional.lastName}:`, whatsappError);
              // Continue execution even if WhatsApp fails
            }
          }

          // 2. Create quote in database (critical - should always execute)
          const quoteData = {
            status: { id: QuoteStatusEnum.Pending },
            amount: null,
            purchaseType: { id: PurchaseQuoteTypeEnum.Assign },
            escrow: null,
            job: { id: request.id },
            user: { id: professional.id }
          };

          const quote = await createQuote(quoteData);

          // 3. Create notification for the professional (critical - should always execute)
          if (quote) {
            try {
              const notificationData = {
                title: "New Emergency Service Assignment",
                message: `You have been assigned to a new emergency service request: ${request.title || "Emergency Job"}`,
                url: "/myproposals",
                status: { id: NotificationStatusEnum.Unread },
                priority: { id: NotificationPriorityEnum.High },
                type: { id: NotificationTypeEnum["In-App"] },
                user: { id: professional.id }
              };

              await createUserNotificationAction(notificationData);
            } catch (notificationError) {
              console.warn(`In-app notification failed for ${professional.firstName} ${professional.lastName}:`, notificationError);
              // Quote is created, so we can continue
            }
          }
        } catch (professionalError) {
          console.error(`Failed to assign ${professional.firstName} ${professional.lastName}:`, professionalError);
          // Continue with next professional even if this one fails
        }
      }

      GlobalService.showToast("success", "Success", `Successfully assigned ${selectedProfessionals.size} professional(s)`);
      setSelectedProfessionals(new Set());
      onClose();
      
      if (onAssignmentSuccess) {
        onAssignmentSuccess();
      }
    } catch (error) {
      console.error("Failed to assign professionals:", error);
      GlobalService.showToast("error", "Error", "Failed to assign professionals");
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle single provider assignment
  const handleProviderAssignment = async (professional: any) => {
    try {
      setIsAssigning(true);
      console.log(request.description, "professional");
      
      // 1. Send WhatsApp notification to the professional (non-blocking - errors won't stop the process)
      if (professional.whatsappNumber) {
        try {
          const whatsAppData = {
            to: professional.whatsappNumber,
            var1_name: `${professional.firstName} ${professional.lastName}`,
            var2_jobTitle: request.title || "Emergency Job",
            var3_jobDescription: (request.description || "Emergency service request").replace(/\s*\n\s*/g, " ").trim(),
            var4_clientName: maskPrivateInfo(request.customer?.name, "name"),
            var5_clientPhone: maskPrivateInfo(request.customer?.phone, "phone"),
            var6_clientWhatsapp: maskPrivateInfo(request.customer?.whatsappNumber, "phone"),
            var7_clientEmail: maskPrivateInfo(request.customer?.email, "email"),
            var8_clientAddress: maskPrivateInfo(request.customer?.address, "address"),
            var9_detailsUrl: "https://kokkobeo.com/myproposals"
          };

          await dispatch(assignProfessionalWhatsApp(whatsAppData));
        } catch (whatsappError) {
          console.warn(`WhatsApp notification failed for ${professional.firstName} ${professional.lastName}:`, whatsappError);
          // Continue execution even if WhatsApp fails
        }
      }

      // 2. Create quote in database (critical - should always execute)
      const quoteData = {
        status: { id: QuoteStatusEnum.Pending },
        amount: null,
        purchaseType: { id: PurchaseQuoteTypeEnum.Assign },
        escrow: null,
        job: { id: request.id },
        user: { id: professional.id }
      };

      const quote = await createQuote(quoteData);

      // 3. Create notification for the professional (critical - should always execute)
      if (quote) {
        try {
          const notificationData = {
            title: "New Emergency Service Assignment",
            message: `You have been assigned to a new emergency service request: ${request.title || "Emergency Job"}`,
            url: "/myproposals",
            status: { id: NotificationStatusEnum.Unread },
            priority: { id: NotificationPriorityEnum.High },
            type: { id: NotificationTypeEnum["In-App"] },
            user: { id: professional.id }
          };

          await createUserNotificationAction(notificationData);
        } catch (notificationError) {
          console.warn(`In-app notification failed for ${professional.firstName} ${professional.lastName}:`, notificationError);
          // Quote is created, so we can continue
        }
      }

      GlobalService.showToast("success", "Success", "Provider assigned successfully!");
      
      // Close modal and reset state
      onClose();
      
      // Call success callback if provided
      if (onAssignmentSuccess) {
        onAssignmentSuccess();
      }

    } catch (error) {
      console.error("Failed to assign provider:", error);
      GlobalService.showToast("error", "Error", "Failed to assign provider");
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Assign Emergency Provider</h2>
              <p className="text-sm text-gray-500 mt-1">
                Emergency ID: {request.id} - {request.service}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Select a professional to assign to this emergency request
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium">Emergency Professionals</h3>
              <Badge variant={undefined} className="bg-green-100 text-green-700">
                Total: {emergencyProfessionalsTotal}
              </Badge>
              {selectedProfessionals.size > 0 && (
                <Badge variant={undefined} className="bg-blue-100 text-blue-700">
                  Selected: {selectedProfessionals.size}
                </Badge>
              )}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              {selectedProfessionals.size > 0 && (
                <Button
                  onClick={handleBulkAssignment}
                  disabled={isAssigning}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isAssigning ? "Assigning..." : `Assign ${selectedProfessionals.size} Professional(s)`}
                </Button>
              )}
            </div>
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search professionals by name, email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {(!professionalsLoading && emergencyProfessionals.length === 0) ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Emergency Professionals Found</h3>
              <p className="text-gray-500">Try adjusting your search or filters.</p>
            </div>
          ) : (
            <div className="relative">
              {professionalsLoading && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={
                            emergencyProfessionals.filter(p => p.whatsappNumber).length > 0 &&
                            selectedProfessionals.size === emergencyProfessionals.filter(p => p.whatsappNumber).length
                          }
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Whatsapp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {emergencyProfessionals.map((professional) => (
                      <tr 
                        key={professional.id} 
                        className={`hover:bg-gray-50 ${selectedProfessionals.has(professional.id) ? "bg-blue-50" : ""}`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedProfessionals.has(professional.id)}
                            onChange={() => handleSelectProfessional(professional.id, !!professional.whatsappNumber)}
                            disabled={!professional.whatsappNumber}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {professional?.photo?.path ? (
                              <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                                <img
                                  src={professional.photo.path}
                                  alt={`${professional.firstName} ${professional.lastName}`}
                                  className="h-full w-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = "none";
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-600" />
                              </div>
                            )}
                            <div className="font-medium text-gray-900">
                              {professional.firstName} {professional.lastName}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {professional.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {professional.phone || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            {professional.whatsappNumber ? (
                              <>
                                <span>{professional.whatsappNumber}</span>
                                <Badge variant={undefined} className="bg-green-100 text-green-700 text-xs">
                                  WhatsApp
                                </Badge>
                              </>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {professional.address || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <Button 
                            size="sm" 
                            onClick={() => handleProviderAssignment(professional)}
                            disabled={!professional.whatsappNumber || isAssigning}
                            className={!professional.whatsappNumber ? "opacity-50 cursor-not-allowed" : ""}
                            title={!professional.whatsappNumber ? "Professional must have WhatsApp number to be assigned" : "Assign professional and send WhatsApp notification"}
                          >
                            {isAssigning ? "Assigning..." : professional.whatsappNumber ? "Assign" : "No WhatsApp"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="px-1 py-2 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Page {emergencyProfessionalsPage} of {emergencyProfessionalsTotalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setProfessionalsPage(Math.max(1, professionalsPage - 1))}
                disabled={professionalsPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setProfessionalsPage(professionalsPage + 1)}
                disabled={emergencyProfessionalsPage >= emergencyProfessionalsTotalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyProfessionalsModal;
